/**
 * Beast Games Shopify Theme JavaScript
 * Handles: Header scroll, Mobile menu, Cart, Product variants
 */

(function() {
  'use strict';

  // ============================================
  // Header Scroll Effect
  // ============================================
  const header = document.querySelector('[data-header]');
  let lastScrollY = 0;
  let ticking = false;

  function updateHeader() {
    const scrollY = window.scrollY;

    if (header) {
      // Continuous scroll animation
      // Maximum effect reached at 200px scroll
      const maxScroll = 200;
      const progress = Math.min(scrollY / maxScroll, 1);

      // Dynamic transform: move up to -30px as user scrolls
      const translateY = -30 * progress;

      // Dynamic opacity: from 1 to 0.85 as user scrolls
      const opacity = 1 - (0.15 * progress);

      // Dynamic scale: subtle shrink from 1 to 0.98
      const scale = 1 - (0.02 * progress);

      // Apply inline styles for smooth continuous animation
      header.style.transform = `translateY(${translateY}px) scale(${scale})`;
      header.style.opacity = opacity;

      // Toggle class for other CSS effects (nav height, logo size)
      if (scrollY > 50) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }
    }

    lastScrollY = scrollY;
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(updateHeader);
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  // ============================================
  // Mobile Menu with Focus Trap (Accessibility)
  // ============================================
  const mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
  const mobileDrawer = document.querySelector('[data-mobile-drawer]');
  const mobileDrawerClose = document.querySelector('[data-mobile-drawer-close]');
  const mobileDrawerOverlay = document.querySelector('[data-mobile-drawer-overlay]');
  const menuIcon = document.querySelector('[data-menu-icon]');
  const closeIcon = document.querySelector('[data-close-icon]');

  let lastFocusedElement = null;
  let focusableElements = [];
  let firstFocusable = null;
  let lastFocusable = null;

  function openMobileMenu() {
    const header = document.querySelector('.header');

    mobileDrawer?.classList.add('active');
    mobileDrawerOverlay?.classList.add('active');
    header?.classList.add('header--drawer-open');
    document.body.style.overflow = 'hidden';
    menuIcon?.classList.add('hidden');
    closeIcon?.classList.remove('hidden');

    // Store last focused element
    lastFocusedElement = document.activeElement;

    // Get all focusable elements in drawer
    if (mobileDrawer) {
      focusableElements = Array.from(
        mobileDrawer.querySelectorAll(
          'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      );
      firstFocusable = focusableElements[0];
      lastFocusable = focusableElements[focusableElements.length - 1];

      // Focus first element
      setTimeout(() => firstFocusable?.focus(), 100);
    }
  }

  function closeMobileMenu() {
    const header = document.querySelector('.header');

    mobileDrawer?.classList.remove('active');
    mobileDrawerOverlay?.classList.remove('active');
    header?.classList.remove('header--drawer-open');
    document.body.style.overflow = '';
    menuIcon?.classList.remove('hidden');
    closeIcon?.classList.add('hidden');

    // Return focus to trigger button
    if (lastFocusedElement) {
      lastFocusedElement.focus();
      lastFocusedElement = null;
    }
  }

  // Focus Trap Handler
  function handleMobileMenuTab(e) {
    if (!mobileDrawer?.classList.contains('active')) return;

    if (e.key === 'Tab') {
      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    }
  }

  mobileMenuToggle?.addEventListener('click', () => {
    if (mobileDrawer?.classList.contains('active')) {
      closeMobileMenu();
    } else {
      openMobileMenu();
    }
  });

  mobileDrawerClose?.addEventListener('click', closeMobileMenu);
  mobileDrawerOverlay?.addEventListener('click', closeMobileMenu);

  // Close mobile menu on navigation
  document.querySelectorAll('[data-mobile-drawer] a').forEach(link => {
    link.addEventListener('click', closeMobileMenu);
  });

  // Keyboard handlers (Escape + Tab Trap)
  document.addEventListener('keydown', (e) => {
    if (mobileDrawer?.classList.contains('active')) {
      // Close on Escape
      if (e.key === 'Escape') {
        closeMobileMenu();
      }
      // Focus Trap on Tab
      handleMobileMenuTab(e);
    }
  });

  // ============================================
  // Scroll to Top Button
  // ============================================
  const scrollToTopBtn = document.querySelector('[data-scroll-to-top]');

  function updateScrollToTopVisibility() {
    if (window.scrollY > 300) {
      scrollToTopBtn?.classList.add('visible');
    } else {
      scrollToTopBtn?.classList.remove('visible');
    }
  }

  window.addEventListener('scroll', updateScrollToTopVisibility, { passive: true });

  scrollToTopBtn?.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  // ============================================
  // AJAX Cart
  // ============================================
  const BeastCart = {
    /**
     * Add item to cart
     */
    async addItem(variantId, quantity = 1) {
      const formData = {
        items: [{
          id: variantId,
          quantity: quantity
        }]
      };

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          throw new Error('Failed to add item');
        }

        const data = await response.json();
        this.updateCartBadge();
        this.showNotification('Zum Warenkorb hinzugefügt');
        return data;
      } catch (error) {
        console.error('Cart error:', error);
        this.showNotification('Fehler beim Hinzufügen', 'error');
        throw error;
      }
    },

    /**
     * Update cart item quantity
     */
    async updateItem(lineId, quantity) {
      const formData = {
        id: lineId,
        quantity: quantity
      };

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          throw new Error('Failed to update item');
        }

        const data = await response.json();
        this.updateCartBadge();
        return data;
      } catch (error) {
        console.error('Cart error:', error);
        throw error;
      }
    },

    /**
     * Remove item from cart
     */
    async removeItem(lineId) {
      return this.updateItem(lineId, 0);
    },

    /**
     * Get current cart state
     */
    async getCart() {
      const response = await fetch('/cart.js');
      return response.json();
    },

    /**
     * Update cart badge in header
     */
    async updateCartBadge() {
      const cart = await this.getCart();
      const badges = document.querySelectorAll('[data-cart-count]');

      badges.forEach(badge => {
        if (cart.item_count > 0) {
          badge.textContent = cart.item_count > 9 ? '9+' : cart.item_count;
          badge.style.display = '';
        } else {
          badge.style.display = 'none';
        }
      });
    },

    /**
     * Show notification toast
     */
    showNotification(message, type = 'success') {
      // Remove existing notifications
      document.querySelectorAll('.beast-notification').forEach(el => el.remove());

      const notification = document.createElement('div');
      notification.className = `beast-notification beast-notification--${type}`;
      notification.textContent = message;

      document.body.appendChild(notification);

      // Trigger animation
      requestAnimationFrame(() => {
        notification.classList.add('beast-notification--visible');
      });

      // Auto-remove after 3s
      setTimeout(() => {
        notification.classList.remove('beast-notification--visible');
        setTimeout(() => notification.remove(), 300);
      }, 3000);

      // Update ARIA Live Region for screen readers
      const ariaAnnouncement = document.querySelector('[data-cart-announcement]');
      if (ariaAnnouncement) {
        ariaAnnouncement.textContent = message;
        // Clear announcement after 5s
        setTimeout(() => {
          ariaAnnouncement.textContent = '';
        }, 5000);
      }
    }
  };

  // Expose to global scope
  window.BeastCart = BeastCart;

  // ============================================
  // Product Variant Selection
  // ============================================
  document.querySelectorAll('[data-product-form]').forEach(form => {
    const variantsJson = form.closest('[data-product-card]')?.querySelector('[data-product-variants]');
    if (!variantsJson) return;

    let variants;
    try {
      variants = JSON.parse(variantsJson.textContent);
    } catch (e) {
      console.error('Failed to parse variants:', e);
      return;
    }

    const optionSelects = form.querySelectorAll('[data-option-select]');
    const variantIdInput = form.querySelector('[data-variant-id]');
    const addToCartBtn = form.querySelector('[data-add-to-cart]');

    function updateVariant() {
      const selectedOptions = [];
      optionSelects.forEach(select => {
        selectedOptions[select.dataset.optionIndex] = select.value;
      });

      // Find matching variant
      const matchingVariant = variants.find(variant => {
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });

      if (matchingVariant) {
        variantIdInput.value = matchingVariant.id;

        // Update availability
        if (addToCartBtn) {
          if (matchingVariant.available) {
            addToCartBtn.disabled = false;
          } else {
            addToCartBtn.disabled = true;
          }
        }

        // Update price if displayed
        const priceEl = form.closest('[data-product-card]')?.querySelector('.product-card__price-current');
        if (priceEl && matchingVariant.price) {
          const formattedPrice = (matchingVariant.price / 100).toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
          priceEl.innerHTML = `${formattedPrice}<span class="product-card__price-currency">€</span>`;
        }
      }
    }

    optionSelects.forEach(select => {
      select.addEventListener('change', updateVariant);
    });

    // Handle add to cart
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const variantId = variantIdInput.value;
      const quantityInput = form.querySelector('[data-quantity-input]');
      const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

      if (!variantId) return;

      // Show loading state
      const cartIcon = addToCartBtn?.querySelector('[data-cart-icon]');
      const spinner = addToCartBtn?.querySelector('[data-loading-spinner]');

      if (spinner) spinner.classList.remove('hidden');
      if (cartIcon) cartIcon.classList.add('hidden');
      if (addToCartBtn) addToCartBtn.disabled = true;

      try {
        await BeastCart.addItem(parseInt(variantId, 10), quantity);
      } finally {
        // Reset button state
        if (spinner) spinner.classList.add('hidden');
        if (cartIcon) cartIcon.classList.remove('hidden');
        if (addToCartBtn) addToCartBtn.disabled = false;
      }
    });
  });

  // ============================================
  // CountUp Stats Animation
  // ============================================
  const CountUpStats = {
    duration: 2000, // Animation duration in ms

    /**
     * Animate a number from 0 to target
     */
    animate(element, target) {
      const start = 0;
      const startTime = performance.now();
      const suffix = element.dataset.suffix || '';
      const prefix = element.dataset.prefix || '';

      const update = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / this.duration, 1);

        // Easing function (easeOutQuart)
        const eased = 1 - Math.pow(1 - progress, 4);
        const current = Math.floor(start + (target - start) * eased);

        element.textContent = prefix + current.toLocaleString('de-DE') + suffix;

        if (progress < 1) {
          requestAnimationFrame(update);
        } else {
          element.textContent = prefix + target.toLocaleString('de-DE') + suffix;
        }
      };

      requestAnimationFrame(update);
    },

    /**
     * Initialize all countup elements
     */
    init() {
      const countupElements = document.querySelectorAll('[data-countup]');

      if (countupElements.length === 0) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const element = entry.target;
            const target = parseInt(element.dataset.countup, 10);

            if (!isNaN(target) && !element.dataset.counted) {
              element.dataset.counted = 'true';
              this.animate(element, target);
            }

            observer.unobserve(element);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px 0px 0px'
      });

      countupElements.forEach(el => observer.observe(el));
    }
  };

  // ============================================
  // Lazy Loading Images
  // ============================================
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          imageObserver.unobserve(img);
        }
      });
    }, {
      rootMargin: '100px 0px'
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }

  // ============================================
  // Smooth Scroll for Anchor Links
  // ============================================
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const href = this.getAttribute('href');
      if (href === '#') return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        const headerHeight = header?.offsetHeight || 0;
        const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    });
  });

  // ============================================
  // Initialize on DOM Ready
  // ============================================
  document.addEventListener('DOMContentLoaded', () => {
    // Update cart badge on load
    BeastCart.updateCartBadge();

    // Initial header state
    updateHeader();

    // Initialize CountUp Stats
    CountUpStats.init();
  });

})();

// ============================================
// AGE VERIFICATION UTILITY
// ============================================
window.BeastAge = {
  /**
   * Calculate exact age in years at event date (Beast Games 2026: May 30-31, 2026)
   * @param {string|Date} birthdate - Birthdate as string (YYYY-MM-DD) or Date object
   * @returns {number} Age in years at event date
   */
  calculateAge(birthdate) {
    // Event date: Beast Games 2026 (May 30-31, 2026)
    const eventDate = new Date('2026-05-30');
    const birth = new Date(birthdate);

    // Validate input
    if (isNaN(birth.getTime())) {
      console.error('Invalid birthdate:', birthdate);
      return -1; // Invalid date
    }

    // Calculate year difference
    let age = eventDate.getFullYear() - birth.getFullYear();

    // Adjust if birthday hasn't occurred at event date yet
    const monthDiff = eventDate.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && eventDate.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  },

  /**
   * Validate age against minimum requirement
   * @param {string|Date} birthdate - Birthdate
   * @param {number} minAge - Minimum required age (default 18)
   * @returns {object} { valid: boolean, age: number }
   */
  validate(birthdate, minAge = 18) {
    const age = this.calculateAge(birthdate);

    return {
      valid: age >= minAge,
      age: age,
      minAge: minAge
    };
  }
};

/**
 * AGE VERIFICATION - Event Listeners
 * Validates birthdate field with visual feedback (red/green border)
 */
(function initAgeVerification() {
  const birthdateField = document.querySelector('[data-age-verify]');
  if (!birthdateField) return; // Not on product page

  const errorDiv = document.getElementById('birthdate-error');
  const productForm = birthdateField.closest('form');

  // Validate on blur (user leaves field)
  birthdateField.addEventListener('blur', () => validateAge());

  // Validate on change (date picker closes)
  birthdateField.addEventListener('change', () => validateAge());

  // Re-validate on input (if previously invalid)
  birthdateField.addEventListener('input', () => {
    if (birthdateField.classList.contains('is-invalid')) {
      validateAge();
    }
  });

  // Form submit validation
  if (productForm) {
    productForm.addEventListener('submit', (e) => {
      const birthdate = birthdateField.value;

      // If empty, let HTML5 required validation handle it
      if (!birthdate) return;

      const result = window.BeastAge.validate(birthdate, 18);

      if (!result.valid) {
        e.preventDefault();
        alert('Du musst am Eventdatum (30.-31. Mai 2026) mindestens 18 Jahre alt sein, um an Beast Games teilzunehmen.');
        birthdateField.focus();
        validateAge(); // Show visual error
      }
    });
  }

  function validateAge() {
    const birthdate = birthdateField.value;

    // If empty, remove all validation states
    if (!birthdate) {
      birthdateField.classList.remove('is-invalid', 'is-valid');
      errorDiv?.classList.remove('visible');
      return;
    }

    // Validate using BeastAge utility
    const result = window.BeastAge.validate(birthdate, 18);

    if (result.valid) {
      // SUCCESS: >= 18 years old
      birthdateField.classList.remove('is-invalid');
      birthdateField.classList.add('is-valid');
      errorDiv?.classList.remove('visible');
    } else {
      // FAILURE: < 18 years old
      birthdateField.classList.remove('is-valid');
      birthdateField.classList.add('is-invalid');
      errorDiv?.classList.add('visible');

      // Screen reader announcement
      if (errorDiv) {
        const announcement = 'Altersverifizierung fehlgeschlagen. Du musst am Eventdatum mindestens 18 Jahre alt sein.';
        errorDiv.setAttribute('aria-live', 'assertive');

        // Announce to screen reader via live region
        const liveRegion = document.getElementById('cart-announcements');
        if (liveRegion) {
          liveRegion.textContent = announcement;
        }
      }
    }
  }
})();

/**
 * Notification Styles (injected dynamically)
 */
const notificationStyles = document.createElement('style');
notificationStyles.textContent = `
  .beast-notification {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    padding: 1rem 1.5rem;
    background: #1e1e1e;
    border-left: 3px solid #c00000;
    border-radius: 0.5rem;
    color: white;
    font-size: 0.875rem;
    z-index: 9999;
    opacity: 0;
    transform: translateY(1rem);
    transition: opacity 0.3s ease, transform 0.3s ease;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  }

  .beast-notification--visible {
    opacity: 1;
    transform: translateY(0);
  }

  .beast-notification--error {
    border-color: #ef4444;
  }

  .beast-notification--success {
    border-color: #22c55e;
  }

  @media (max-width: 640px) {
    .beast-notification {
      left: 1rem;
      right: 1rem;
      bottom: 1rem;
    }
  }
`;
document.head.appendChild(notificationStyles);

/**
 * WIDERRUFSBUTTON - ORDER SELECTION & PRE-FILL
 * Gesetzlich verpflichtend ab 19.06.2026 (§ 356a BGB)
 * Two-stage withdrawal process with smart pre-fill
 */
(function initWiderrufsformular() {
  const orderSelect = document.getElementById('order-select');
  if (!orderSelect) return; // Not on Widerrufsformular page

  // Form fields
  const orderPreview = document.getElementById('order-preview');
  const nameField = document.getElementById('contact-name');
  const emailField = document.getElementById('contact-email');
  const addressField = document.getElementById('contact-address');
  const orderNumberField = document.getElementById('contact-order');
  const productField = document.getElementById('contact-product');
  const dateField = document.getElementById('contact-date');
  const confirmCheckbox = document.getElementById('contact-confirm');
  const submitButton = document.querySelector('.btn-widerruf');
  const liveRegion = document.getElementById('widerruf-announcements');

  // Event Listener for Order Selection
  orderSelect.addEventListener('change', function() {
    const selectedOption = this.options[this.selectedIndex];

    if (!selectedOption.value) {
      // No selection - hide preview and clear form
      if (orderPreview) orderPreview.style.display = 'none';
      clearFormFields();
      announceToScreenReader('Auswahl zurückgesetzt');
      return;
    }

    // Extract data from data-* attributes
    const orderNumber = selectedOption.value;
    const orderDate = selectedOption.dataset.orderDate;
    const orderEmail = selectedOption.dataset.orderEmail;
    const orderName = selectedOption.dataset.orderName;
    const orderAddress = selectedOption.dataset.orderAddress;
    const orderProducts = selectedOption.dataset.orderProducts;

    // Update preview card
    if (orderPreview) {
      document.getElementById('preview-order-number').textContent = orderNumber;
      document.getElementById('preview-order-date').textContent = orderDate;
      document.getElementById('preview-order-products').textContent = orderProducts;
      orderPreview.style.display = 'block';
    }

    // Pre-fill form fields
    if (nameField) nameField.value = orderName || '';
    if (emailField) emailField.value = orderEmail || '';
    if (addressField) addressField.value = orderAddress || '';
    if (orderNumberField) orderNumberField.value = orderNumber || '';
    if (productField) productField.value = orderProducts || '';

    // Convert date from DD.MM.YYYY to YYYY-MM-DD (HTML5 date input format)
    if (dateField && orderDate) {
      const dateParts = orderDate.split('.');
      if (dateParts.length === 3) {
        const [day, month, year] = dateParts;
        dateField.value = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    // Uncheck confirmation (user must actively confirm)
    if (confirmCheckbox) {
      confirmCheckbox.checked = false;
    }

    // Screen reader announcement
    announceToScreenReader(`Bestellung ${orderNumber} ausgewählt. Formular wurde automatisch ausgefüllt. Bitte überprüfen Sie die Angaben und bestätigen Sie den Widerruf.`);

    // Smooth scroll to form after short delay
    setTimeout(() => {
      const formElement = document.getElementById('widerruf-form');
      if (formElement) {
        formElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 400);

    // Visual feedback on submit button
    if (submitButton) {
      submitButton.style.animation = 'pulse 1s ease-in-out 2';
    }
  });

  // Helper: Clear all form fields
  function clearFormFields() {
    if (nameField) nameField.value = '';
    if (emailField) emailField.value = '';
    if (addressField) addressField.value = '';
    if (orderNumberField) orderNumberField.value = '';
    if (productField) productField.value = '';
    if (dateField) dateField.value = '';
    if (confirmCheckbox) confirmCheckbox.checked = false;
  }

  // Helper: Screen reader announcements
  function announceToScreenReader(message) {
    if (liveRegion) {
      liveRegion.textContent = message;
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 5000);
    }
  }

  // Validation enhancement: Prevent submission without confirmation
  const form = document.getElementById('widerruf-form');
  if (form) {
    form.addEventListener('submit', function(e) {
      if (confirmCheckbox && !confirmCheckbox.checked) {
        e.preventDefault();
        alert('Bitte bestätigen Sie den Widerruf durch Aktivieren der Checkbox.');
        confirmCheckbox.focus();
        return false;
      }
    });
  }

  // Add pulse animation to submit button
  const style = document.createElement('style');
  style.textContent = `
    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.02); box-shadow: 0 0 30px rgba(192, 0, 0, 0.6); }
    }
  `;
  document.head.appendChild(style);
})();
